import { canWrap } from "../../../utils/zone.utils";
import { SYM_LAYER_INDENTIFY, SYM_SPECIAL_DEPS } from "../../enum/Symbol";
import PropArray from "../../type/propArray";
import useOnLayerAfterSet from "../observable/useOnLayerAfterSet";
import { getLayerUID, isLayer } from "./validator/useLayerIdentity";
import { getRootProps, useMarkRootProp, useMarkPropIsRoot } from "./store/useStoreRoot";
import useArrayWatcher from "../watcher/useArrayWatcher";
import useObjectWatcher from "../watcher/useObjectWatcher";
import useQueryCopySource from "./store/useStoreCopySource";
import { storeParentLayer } from "./store/useStoreLayerParent";
import { storePathByLayer } from "./store/useStorePathByLayer";
import useQueryOriginalSourceByLayer from "./store/useStoreOriginalSource";
import useQueryOriginalSourceRefLayer, { storeLayerUsingOriginalSource } from "./store/useStoreLayerUsingOriginalSource";
import { storeRootLayer, useMarkRootLayer } from "./store/useStoreRootLayer";
import useLayerChain from "./useLayerChain";
import { useQueryLayer1 } from "./useQueryLayer";

const useAssignPropsToParent = (chainId, propertyPath, fromSource, layer) => {
    /* Check if this has parent, assign this prop to parent */
    const { queryParentLayerByPath, hasParentLayer } = useLayerChain(chainId)
    if (!hasParentLayer(propertyPath)) return;
    const parentLayer = queryParentLayerByPath(propertyPath);
    const prop = propertyPath.last();
    if (propertyPath.length > 0) {
        parentLayer[prop] = layer;
        // parent2[prop] = layer2;
    } else {
        const originSource = useQueryOriginalSourceByLayer(parentLayer)
        for (let k in fromSource) {
            if (originSource[k] === undefined) {
                originSource[k] = fromSource[k];
            }
            if (!canWrap(fromSource[k])) continue;
            parentLayer[k] = layer[k];
        }
    }
}


const genLayerId = (() => {
    let count = 0;
    return () => '#' + (++count);
})();

const bootstrapSource = (watcherOptions) => {
    const watcherOutput = useObjectWatcher(watcherOptions);

    const copySource1 = watcherOutput.copySource;
    return watcherOutput;
}

const isAvoidSpecialKeyForArray = (key) => key === SYM_SPECIAL_DEPS

const bootstrapArray = (propertyPath, fromSource, layer, chainId) => {

    const { getRootLayer, getRootSource } = useLayerChain(chainId)
    const copySource = useQueryCopySource(layer);
    const rootSource = getRootSource();
    const rootLayer = getRootLayer();

    for (let k in fromSource) {
        if (isAvoidSpecialKeyForArray(k) || !Array.isArray(fromSource[k])) {
            continue;
        }
        const paths = propertyPath.clone();
        paths.push(k)
        const proxyArray1 = useArrayWatcher(paths, layer, fromSource[k], chainId);
        copySource[k] = proxyArray1;
        storeLayerUsingOriginalSource(fromSource[k], layer, k);
        if (rootSource && propertyPath.length === 0) {
            rootSource[k] = proxyArray1;
            storeLayerUsingOriginalSource(fromSource[k], getRootLayer(), k);
        }
    }

    return layer
}

const useReuseLayer = (fromSource) => {
    if (!isLayer(fromSource)) {
        return [false, null];
    }

    const l1 = useQueryLayer1(fromSource)
    if (l1) {
        const root = getRootProps(l1)
        return [true, [l1, fromSource]]
    }
    return [false, null]
}

const useChainCreator = (parentChainId) => {
    const {
        storeLayer,
        hasLayer,
        getOriginalSource,
        getChainId,
        discardChain,
        getRootLayer,
        getRootSource,
        storeLinkingOriginalSource,
        storeLinkingLayer
    } = useLayerChain();

    const chainId = getChainId();


    /**
     * 
     * @param {object} fromSource 
     * @param {IPropArray} propertyPath 
     * @param {string} chainId 
     * 
     * @returns {[typeof Proxy, typeof Proxy]}
     */
    const newLayer = (fromSource = rootSource, propertyPath = new PropArray()) => {
        if (!canWrap(fromSource)) {
            throw new Error(`Cannot wrap ${fromSource}`)
        }

        // const [canReuse, reuseLayers] = useReuseLayer(fromSource, getRootLayer());
        // if (canReuse) {
        //     return reuseLayers;
        // }
        const childWappers = {};

        // This source append to Root
        const originSource = getOriginalSource();
        const rootSource = getRootSource();
        const rootLayer = getRootLayer();
        if (propertyPath.length === 0 && originSource) {
            Object.assign(originSource, fromSource);
            Object.assign(rootSource, fromSource);
        }

        const wops = { originalSource: fromSource, propertyPath: propertyPath.clone(), chainId: getChainId() }
        const { layer, copySource } = bootstrapSource(wops);

        /** @type {WatcherHookOption} */
        if (rootLayer) {
            useMarkRootProp(wops.originalSource, rootLayer);
        }

        /* Check if has parent, map these prop to parent current Proxy*/
        useAssignPropsToParent(getChainId(), propertyPath, fromSource, layer)



        if (propertyPath.length === 0 && !originSource) {
            storeRootLayer(layer);
            useMarkPropIsRoot(copySource, layer)
        } else if (rootLayer) {
            useMarkRootLayer(layer, rootLayer)
        }

        if (rootLayer) {
            useMarkRootProp(layer, rootLayer)
        }

        storeLayer(propertyPath, fromSource, copySource, layer);

        if (parentChainId) {
            useLayerChain(parentChainId).storeSubChain(chainId);
        }

        if (originSource && propertyPath.length === 0) {
            const root = getRootSource();
            for (let k in childWappers) {
                root[k] = childWappers[k];
            }
        }

        for (let k in fromSource) {
            if (!canWrap(fromSource[k])) continue;
            const cloneProps = propertyPath.clone();
            cloneProps.push(k);
            const [subLayer1] = newLayer(fromSource[k], cloneProps);
            copySource[k] = subLayer1
            storeParentLayer(subLayer1, layer);
        }


        bootstrapArray(propertyPath, fromSource, layer, parentChainId || chainId);
        return [layer]
    }

    return {
        newLayer,
        discardChain,
        getOriginalSource,
        getRootLayer,
        getChainId,
    }
}

export default useChainCreator